import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  describePipelineRun,
  getPipelineRunStatus,
  listPipelineRunFiles,
  presignPipelineRunFile,
  type PipelineRunDetail,
  type PipelineRunFile,
  type PipelineRunStatus,
  type PipelineRunSummary,
} from '../../api/taxPipeline';
import { formatTimestamp, formatUsd } from './taxReportingUtils';

// =============================================================================
// PipelineRunDetailPane (CTR — redesign_pipeline_runs_ux TASK-006)
//
// In-place expand pane rendered as a DataTable expandedRowContent under a
// pipeline-run row. Owns its own queries — TASK-008 mounts it from
// PipelineRunsView's expandedRowContent callback.
// =============================================================================

// Exported so PipelineRunsView's list-level polling predicate reuses the same
// terminal-status set as the detail pane — one source of truth (CTR-018 AC-4).
export const TERMINAL_STATUSES = ['SUCCEEDED', 'FAILED', 'TIMED_OUT', 'ABORTED'] as const;

export function isTerminal(status: PipelineRunStatus | undefined): boolean {
  return status !== undefined && TERMINAL_STATUSES.includes(status as (typeof TERMINAL_STATUSES)[number]);
}

// Single source of truth for pipeline-run status badge classes; PipelineRunsView
// imports this rather than re-declaring it (CTR-018 review follow-up).
export const STATUS_BADGE_CLASS: Record<PipelineRunStatus, string> = {
  RUNNING: 'badge badge-status badge-running',
  SUCCEEDED: 'badge badge-status badge-succeeded',
  FAILED: 'badge badge-status badge-failed',
  TIMED_OUT: 'badge badge-status badge-timed-out',
  ABORTED: 'badge badge-status badge-aborted',
};

/** Humanize a byte count as B/KB/MB/GB. */
function formatBytes(size: number): string {
  if (!Number.isFinite(size) || size < 0) return '—';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

interface PipelineRunDetailPaneProps {
  executionName: string;
}

export const PipelineRunDetailPane: React.FC<PipelineRunDetailPaneProps> = ({
  executionName,
}) => {
  const queryClient = useQueryClient();

  // Manifest / bulk detail: fetched once, never polled. We invalidate this
  // key from the status-query effect when the run hits a terminal state so
  // the manifest (methods, failure) refreshes on transition.
  const detailQuery = useQuery({
    queryKey: ['tax', 'pipeline-run-detail', executionName],
    queryFn: ({ signal }) => describePipelineRun(executionName, signal),
    enabled: true,
    staleTime: 30_000,
    retry: 1,
  });

  // Live status: polled every 5s while non-terminal, suppressed on error or
  // terminal state.
  const statusQuery = useQuery({
    queryKey: ['tax', 'pipeline-run-status', executionName],
    queryFn: ({ signal }) => getPipelineRunStatus(executionName, signal),
    enabled: true,
    staleTime: 0,
    retry: 1,
    refetchInterval: (query) =>
      query.state.error || isTerminal(query.state.data?.status) ? false : 5000,
  });

  // When status transitions FROM non-terminal TO terminal, refresh the
  // manifest-backed detail and files queries — the run just finished and
  // their data is now stale.
  const prevStatusRef = React.useRef<PipelineRunStatus | undefined>(undefined);
  React.useEffect(() => {
    const current = statusQuery.data?.status;
    const previous = prevStatusRef.current;
    if (previous && !isTerminal(previous) && current && isTerminal(current)) {
      queryClient.invalidateQueries({
        queryKey: ['tax', 'pipeline-run-detail', executionName],
      });
      queryClient.invalidateQueries({
        queryKey: ['tax', 'pipeline-run-files', executionName],
      });
    }
    prevStatusRef.current = current;
  }, [statusQuery.data?.status, executionName, queryClient]);

  // Status-query is fresher than detailQuery — prefer it for the badge and
  // for terminal-gating, but fall back to detail for first paint when the
  // status query hasn't returned yet.
  const currentStatus: PipelineRunStatus | undefined =
    statusQuery.data?.status ?? detailQuery.data?.status;

  const filesQuery = useQuery({
    queryKey: ['tax', 'pipeline-run-files', executionName],
    queryFn: ({ signal }) => listPipelineRunFiles(executionName, signal),
    enabled: currentStatus ? isTerminal(currentStatus) : false,
    staleTime: 30_000,
    retry: 1,
  });

  const presignMutation = useMutation({
    mutationFn: (s3Key: string) => presignPipelineRunFile(executionName, s3Key),
    onSuccess: (resp) => {
      window.open(resp.url, '_blank');
    },
  });

  if (
    detailQuery.isLoading ||
    (statusQuery.isLoading && !detailQuery.data)
  ) {
    return (
      <div className="pipeline-run-detail-pane__loading">Loading run detail…</div>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    const msg =
      detailQuery.error instanceof Error
        ? detailQuery.error.message
        : 'Failed to load run detail.';
    return (
      <div className="pipeline-run-detail-pane__error" role="alert">
        Error: {msg}
      </div>
    );
  }

  const detail: PipelineRunDetail = detailQuery.data;
  const status: PipelineRunStatus = currentStatus ?? detail.status;
  const files: PipelineRunFile[] = filesQuery.data ?? [];

  // Group files by method. Falls back to a single "—" group when files
  // arrive without a method (e.g., legacy runs without a manifest).
  const filesByMethod = files.reduce<Record<string, PipelineRunFile[]>>((acc, f) => {
    const k = f.method || '—';
    (acc[k] ??= []).push(f);
    return acc;
  }, {});

  // Per-method summary stats from the manifest object form. Empty for
  // legacy runs whose `methods` is a bare string array.
  const summaries: Record<string, PipelineRunSummary> =
    detail.methods != null && !Array.isArray(detail.methods)
      ? Object.fromEntries(
          Object.entries(detail.methods).map(([m, v]) => [m, v.summary])
        )
      : {};

  const handleDownload = (e: React.MouseEvent, s3Key: string) => {
    // Prevent the row's onClick from collapsing the pane mid-mutation.
    e.stopPropagation();
    presignMutation.mutate(s3Key);
  };

  return (
    <div className="pipeline-run-detail-pane">
      <div>
        <span className={STATUS_BADGE_CLASS[status] ?? 'badge'}>{status}</span>
      </div>

      {!isTerminal(status) && (
        <div className="pipeline-run-detail-pane__progress">
          <h4>Live Progress</h4>
          <dl className="pipeline-run-detail-pane__kv">
            <dt>Current step</dt>
            <dd>{statusQuery.data?.currentStateName ?? '—'}</dd>
            <dt>Last activity</dt>
            <dd>
              {statusQuery.data?.lastEventAt
                ? formatTimestamp(statusQuery.data.lastEventAt)
                : '—'}
              {' ('}
              {statusQuery.data?.lastEventType ?? '—'}
              {')'}
            </dd>
          </dl>
        </div>
      )}

      {detail.failure && (
        <div className="pipeline-run-detail-pane__failure" role="alert">
          <strong>Run failed at step: {detail.failure.failedStep}</strong>
          <div>{detail.failure.message}</div>
          <div>
            <strong>Recommended action:</strong> {detail.failure.recommendedAction}
          </div>
        </div>
      )}

      {isTerminal(status) && (
        <div className="pipeline-run-detail-pane__results">
          {filesQuery.isLoading ? (
            <div>Loading results…</div>
          ) : filesQuery.isError ? (
            <div role="alert">
              Error:{' '}
              {filesQuery.error instanceof Error
                ? filesQuery.error.message
                : 'Failed to load files.'}
            </div>
          ) : Object.keys(filesByMethod).length === 0 ? (
            <div>No files generated.</div>
          ) : (
            Object.entries(filesByMethod).map(([method, methodFiles]) => {
              const s = summaries[method];
              return (
                <section
                  key={method}
                  className="pipeline-run-detail-pane__method-block"
                >
                  <header className="pipeline-run-detail-pane__method-header">
                    <span className="pipeline-run-detail-pane__method-label">
                      {method}
                    </span>
                    {s && (
                      <span className="pipeline-run-detail-pane__method-stats">
                        {s.transactionsImported.toLocaleString()} imported ·{' '}
                        {s.taxLotCount.toLocaleString()} tax lots ·{' '}
                        gain/loss {formatUsd(s.totalGainLoss)}
                      </span>
                    )}
                  </header>
                  <table className="pipeline-run-detail-pane__file-table">
                    <thead>
                      <tr>
                        <th>File</th>
                        <th>Format</th>
                        <th>Size</th>
                        <th aria-label="Download" />
                      </tr>
                    </thead>
                    <tbody>
                      {methodFiles.map((f) => (
                        <tr key={f.s3Key}>
                          <td>{f.name}</td>
                          <td>{f.format}</td>
                          <td>{formatBytes(f.size)}</td>
                          <td>
                            <button
                              type="button"
                              className="modal-pill-button modal-pill-preview"
                              onClick={(e) => handleDownload(e, f.s3Key)}
                              disabled={presignMutation.isPending}
                              aria-label={`Download ${f.name}`}
                            >
                              Download
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default PipelineRunDetailPane;
